import { useCustom, useTranslate, type BaseRecord } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { MediaProjectResponse } from "../../types";
import {
  useAnalyticsDateRange,
  AnalyticsDateRange,
} from "./useAnalyticsDateRange";
import { useAnalyticsBack } from "./useAnalyticsBack";
import { ANALYTICS_STALE_TIME } from "../../components/analytics/constants/constants";
import {
  buildDateFilters,
  buildVersionFilters,
} from "../../components/analytics/utils/";
import {
  useAnalyticsVersion,
  type AnalyticsVersionState,
} from "./useAnalyticsVersion";

export type UseAnalyticsPageResult<T> = {
  t: ReturnType<typeof useTranslate>;
  projectId: string | undefined;
  dateRange: AnalyticsDateRange;
  setDateRange: (
    range:
      | AnalyticsDateRange
      | ((prev: AnalyticsDateRange) => AnalyticsDateRange)
  ) => void;
  project: MediaProjectResponse | undefined;
  summaryData: T | undefined;
  isDataLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  handleBack: (() => void) | undefined;
  /** Whether the summary follows `dateRange` (and the page offers a picker). */
  dateFiltered: boolean;
  /** The picked version, on pages whose data follows it. */
  version: AnalyticsVersionState | undefined;
};

export function useAnalyticsPage<T extends BaseRecord>({
  analyticsType,
  dateFiltered = true,
  versioned = false,
  summaryFollowsVersion = false,
}: {
  analyticsType: string;
  /** False for endpoints that take no date filter. */
  dateFiltered?: boolean;
  /** True for pages whose data follows the picked version. */
  versioned?: boolean;
  /** True when the summary endpoint takes the version too. */
  summaryFollowsVersion?: boolean;
}): UseAnalyticsPageResult<T> {
  const t = useTranslate();
  const { id: projectId } = useParams<{ id: string }>();
  const { dateRange, setDateRange, project } = useAnalyticsDateRange(projectId);
  // Pages that don't follow a version never load the list.
  const version = useAnalyticsVersion(versioned ? projectId : undefined);
  // An unfiltered summary is keyed on no date, so a range picked on another
  // tab doesn't refetch the same data.
  const dateFilters = dateFiltered ? buildDateFilters(dateRange) : [];
  const dateEnabled =
    !dateFiltered || (!!dateRange.startDate && !!dateRange.endDate);

  const {
    data: summary,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  } = useCustom<T>({
    url: `analytics/projects/${projectId}/analytics/${analyticsType}/summary`,
    method: "get",
    config: {
      filters: [
        ...dateFilters,
        ...buildVersionFilters(
          summaryFollowsVersion ? version.requested : undefined
        ),
      ],
    },
    queryOptions: {
      enabled: !!projectId && dateEnabled,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const summaryData = summary?.data ?? undefined;
  const isDataLoading = isLoading || isFetching;
  const handleBack = useAnalyticsBack();

  return {
    t,
    projectId,
    dateRange,
    setDateRange,
    project,
    summaryData,
    isDataLoading,
    isSuccess,
    isError,
    handleBack,
    dateFiltered,
    version: versioned ? version : undefined,
  };
}
